import React from "react";
import { Mail, Linkedin, ArrowUpRight } from "lucide-react";
import profile from "../../../data/profile.json";

/**
 * Slim autoplaying marquee of testimonial one-liners with a "Let's talk"
 * CTA underneath. Sits above the footer as a proof-strip for scanners.
 * Pauses on hover. Content duplicated once so the CSS keyframe loops
 * seamlessly (translateX 0 → -50%).
 */
export default function TestimonialsMarquee() {
  const items = (profile.testimonials?.items || []).map((t) => ({
    name: t.name,
    quote: firstSentence(t.quote),
  }));

  if (items.length === 0) return null;

  const loop = [...items, ...items];
  const close = profile.close || {};

  return (
    <section
      aria-label="Client testimonials and contact"
      className="relative overflow-hidden py-10 md:py-12"
      style={{ background: "#E4DAC1" }}
      data-testid="testimonials-marquee"
    >
      <p
        className="mb-6 md:mb-8 text-center uppercase"
        style={{
          fontFamily: "'Space Grotesk', system-ui, sans-serif",
          fontSize: "12px",
          letterSpacing: "0.24em",
          color: "#8A7E6B",
          fontWeight: 700,
        }}
      >
        In their words
      </p>

      {/* Edge fades */}
      <div
        className="pointer-events-none absolute left-0 top-0 bottom-[168px] md:bottom-[180px] w-24 md:w-40 z-10"
        style={{ background: "linear-gradient(to right, #E4DAC1 0%, transparent 100%)" }}
      />
      <div
        className="pointer-events-none absolute right-0 top-0 bottom-[168px] md:bottom-[180px] w-24 md:w-40 z-10"
        style={{ background: "linear-gradient(to left, #E4DAC1 0%, transparent 100%)" }}
      />

      <div className="rp-marquee">
        <div className="rp-marquee-track">
          {loop.map((t, i) => (
            <a
              key={i}
              href="/#more-testimonials"
              className="rp-marquee-card group"
              data-testid={i < items.length ? `marquee-item-${i}` : undefined}
              aria-hidden={i >= items.length}
              aria-label={`Testimonial by ${t.name}`}
            >
              <span className="rp-marquee-body">
                <span className="rp-marquee-quote">&ldquo;{t.quote}&rdquo;</span>
                <span className="rp-marquee-name">{t.name}</span>
              </span>
            </a>
          ))}
        </div>
      </div>

      {/* Let's Talk CTA */}
      <div
        className="mt-10 md:mt-14 max-w-3xl mx-auto px-6 text-center"
        data-testid="lets-talk"
      >
        <p
          className="uppercase mb-3"
          style={{
            fontFamily: "'Space Grotesk', system-ui, sans-serif",
            fontSize: "11px",
            letterSpacing: "0.28em",
            color: "#B5793F",
            fontWeight: 700,
          }}
        >
          Ready to build?
        </p>
        <p
          className="rp-display rp-italic leading-[1.05] mb-6"
          style={{
            fontSize: "clamp(2.25rem, 4vw, 3.25rem)",
            color: "#262016",
            textWrap: "balance",
          }}
        >
          {close.line || "Let's talk."}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <a
            href={`mailto:${close.email || "hi@rosh.work"}`}
            data-testid="lets-talk-email"
            className="group inline-flex items-center gap-2.5 rounded-full px-6 py-3 rp-display font-semibold text-[15px] tracking-tight transition-transform hover:-translate-y-0.5 shadow-[0_16px_36px_-14px_rgba(38,32,22,0.55)]"
            style={{
              background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)",
              color: "#F6F1E5",
              border: "1px solid #B5793F",
            }}
          >
            <Mail size={17} style={{ color: "#EAB16A" }} />
            <span>{close.email || "hi@rosh.work"}</span>
            <ArrowUpRight size={17} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" style={{ color: "#EAB16A" }} />
          </a>
          <a
            href={close.linkedin || "https://www.linkedin.com/in/roshanraykar"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="lets-talk-linkedin"
            className="inline-flex items-center gap-2.5 rounded-full px-6 py-3 rp-display font-medium text-[15px] tracking-tight border transition-colors"
            style={{
              color: "#262016",
              borderColor: "rgba(38,32,22,0.28)",
              background: "rgba(255,253,246,0.6)",
            }}
          >
            <Linkedin size={17} />
            <span>Connect on LinkedIn</span>
          </a>
        </div>
      </div>
    </section>
  );
}

// Return the FIRST full sentence of the quote (falls back to shortening if
// no sentence terminator is found within 220 chars).
function firstSentence(text) {
  if (!text) return "";
  const clean = String(text).replace(/\s+/g, " ").trim();
  // Prefer sentence boundary — take up to the first ., !, or ?
  const m = clean.match(/^(.+?[.!?])(\s|$)/);
  if (m) {
    const sentence = m[1];
    // If the sentence itself is very long, keep it up to the next natural
    // clause break so cards stay compact.
    if (sentence.length > 200) return sentence.slice(0, 198).replace(/[,;:\s]\S*$/, "") + "…";
    return sentence;
  }
  return clean.length <= 200 ? clean : clean.slice(0, 198).replace(/[,;:\s]\S*$/, "") + "…";
}
